import React, { useState } from 'react';
import FileArea from '../FileArea';
import {
  InformationCircleIcon,
  DocumentArrowUpIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  IdentificationIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  UserPlusIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  TableCellsIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import { ArrowRightIcon, ChevronDownIcon } from '@heroicons/react/20/solid';

const MemberUploadPage = ({
  fund,
  errors,
  exampleSpreadsheetPath,
  importInvoicingMembersPath,
  membersPath,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [createInvoices, setCreateInvoices] = useState(false);
  const [sendEmails, setSendEmails] = useState(false);
  const [lookupBy, setLookupBy] = useState('email');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleSubmit = (e) => {
    setIsLoading(true);
    // Let the form submit normally
  };

  const requiredColumns = ['Membership ID', 'First Name', 'Last Name', 'Email', 'Dues Amount'];
  const optionalColumns = ['Line 1', 'Line 2', 'City', 'State', 'Zip', 'Country', 'Home Phone', 'Cell Phone', 'Balance', 'Due Date', 'Invoice Title'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-6">
          {/* Breadcrumb */}
          <nav className="hidden sm:flex mb-4" aria-label="Breadcrumb">
            <ol role="list" className="flex items-center space-x-2">
              <li>
                <a href="/dashboard" className="text-gray-400 hover:text-gray-500 transition-colors">
                  <HomeIcon className="w-4 h-4" />
                </a>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <a href="/dashboard/invoicing" className="ml-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                    Invoicing
                  </a>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <a href={membersPath} className="ml-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                    {fund.name}
                  </a>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-2 text-sm font-medium text-gray-700">Upload Members</span>
                </div>
              </li>
            </ol>
          </nav>

          {/* Header with title */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <CloudArrowUpIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Upload Members</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Import members from a spreadsheet into <span className="font-medium text-gray-700">{fund.name}</span>
                </p>
              </div>
            </div>

            {/* Quick action: Add single member */}
            <a
              href={membersPath + '/new'}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
            >
              <UserPlusIcon className="w-4 h-4" />
              Add Single Member
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-6">
        <div className="max-w-4xl mx-auto">
          <form
            action={importInvoicingMembersPath}
            method="post"
            data-turbo="false"
            encType="multipart/form-data"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <input
              type="hidden"
              name="authenticity_token"
              value={document.getElementsByName('csrf-token')[0]?.content}
            />

            {/* Step 1: Upload File */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-transparent rounded-t-xl">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-600 text-white text-sm font-bold">
                    1
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Select Your Spreadsheet</h2>
                    <p className="text-sm text-gray-500">Upload an Excel or CSV file with member data</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <FileArea
                  name="spreadsheet"
                  initialValue={null}
                  hasMaxSize={false}
                  buttonText="Drag & Drop Spreadsheet Here"
                  previewType="name"
                  errors={errors || []}
                />

                <div className="mt-5 flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <TableCellsIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-600">
                    Need a template?{' '}
                    <a href={`${exampleSpreadsheetPath}.xlsx`} className="text-indigo-600 hover:text-indigo-500 font-medium">
                      Download example.xlsx
                    </a>
                  </span>
                </div>

                {/* Required Columns */}
                <div className="mt-5 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <p className="text-sm font-semibold text-blue-900 mb-3">Required columns:</p>
                  <div className="flex flex-wrap gap-2">
                    {requiredColumns.map((col) => (
                      <span key={col} className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium bg-white text-blue-700 border border-blue-200 shadow-sm">
                        {col}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-3">
                    <span className="font-medium">Optional:</span> {optionalColumns.join(', ')}
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2: Configure Options */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-transparent rounded-t-xl">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-600 text-white text-sm font-bold">
                    2
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Configure Import Options</h2>
                    <p className="text-sm text-gray-500">Choose what happens after members are imported</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-5">
                {/* Create Invoices Option */}
                <div className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all ${createInvoices ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${createInvoices ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                    <DocumentTextIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-semibold ${createInvoices ? 'text-indigo-900' : 'text-gray-900'}`}>
                        Create Invoices
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="hidden"
                          name="create_invoices"
                          value="false"
                        />
                        <input
                          type="checkbox"
                          name="create_invoices"
                          value="true"
                          checked={createInvoices}
                          onChange={(e) => setCreateInvoices(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                    <p className={`mt-1.5 text-sm ${createInvoices ? 'text-indigo-700' : 'text-gray-500'}`}>
                      An invoice will be created for each member using their <strong>Dues Amount</strong> from the spreadsheet.
                    </p>
                    {createInvoices && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-indigo-200">
                        <div className="flex items-start gap-2">
                          <InformationCircleIcon className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-indigo-700">
                            <strong>Tip:</strong> Include a "Due Date" column to set custom due dates, or a "Balance" column for specific amounts.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Send Emails Option */}
                <div className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all ${sendEmails ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${sendEmails ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                    <EnvelopeIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-semibold ${sendEmails ? 'text-amber-900' : 'text-gray-900'}`}>
                        Send Notification Emails
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="hidden"
                          name="send_emails"
                          value="false"
                        />
                        <input
                          type="checkbox"
                          name="send_emails"
                          value="true"
                          checked={sendEmails}
                          onChange={(e) => setSendEmails(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                      </label>
                    </div>
                    <p className={`mt-1.5 text-sm ${sendEmails ? 'text-amber-700' : 'text-gray-500'}`}>
                      Members will receive email notifications about their new invoices. New members also get welcome emails.
                    </p>
                    {sendEmails && !createInvoices && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-amber-200">
                        <div className="flex items-start gap-2">
                          <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-amber-700">
                            <strong>Note:</strong> Invoice emails are only sent when "Create Invoices" is also enabled.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3: Advanced Options (Collapsible) */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full px-6 py-4 flex items-center justify-between rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-400 text-white text-sm font-bold">
                    3
                  </div>
                  <div className="text-left">
                    <h2 className="text-base font-semibold text-gray-900">Advanced: Member Identification</h2>
                    <p className="text-sm text-gray-500">Configure how existing members are matched</p>
                  </div>
                </div>
                <ChevronDownIcon
                  className={`w-5 h-5 text-gray-500 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                />
              </button>

              {showAdvanced && (
                <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                  <p className="text-sm text-gray-600 mb-4">
                    Choose how existing members are identified when updating their information.
                  </p>

                  <div className="space-y-3">
                    {/* Email Option (Default) */}
                    <label
                      className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        lookupBy === 'email' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <input
                        type="radio"
                        name="lookup_by"
                        value="email"
                        checked={lookupBy === 'email'}
                        onChange={(e) => setLookupBy(e.target.value)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <EnvelopeIcon className="h-5 w-5 text-blue-600" />
                          <span className="font-semibold text-gray-900">Identify by Email</span>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">Recommended</span>
                        </div>
                        <p className="mt-1 text-sm text-gray-600">
                          Members are matched by their email address. Existing members will have their info updated.
                        </p>
                        <div className="mt-2 p-2 bg-white rounded-lg border border-gray-200 text-xs text-gray-600">
                          <p><strong>Best for:</strong> Most use cases. Member emails don't change often.</p>
                          <p className="text-amber-600 mt-1"><strong>Note:</strong> Cannot update email addresses in this mode.</p>
                        </div>
                      </div>
                    </label>

                    {/* Membership ID Option */}
                    <label
                      className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        lookupBy === 'membership_id' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <input
                        type="radio"
                        name="lookup_by"
                        value="membership_id"
                        checked={lookupBy === 'membership_id'}
                        onChange={(e) => setLookupBy(e.target.value)}
                        className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <IdentificationIcon className="h-5 w-5 text-purple-600" />
                          <span className="font-semibold text-gray-900">Identify by Membership ID</span>
                        </div>
                        <p className="mt-1 text-sm text-gray-600">
                          Members are matched by their Membership ID. Allows updating any field, including email.
                        </p>
                        <div className="mt-2 p-2 bg-white rounded-lg border border-gray-200 text-xs text-gray-600">
                          <p><strong>Best for:</strong> Updating member emails, or when IDs are your primary identifier.</p>
                        </div>
                        {lookupBy === 'membership_id' && (
                          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <div className="flex gap-2">
                              <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-amber-800">Email Change Behavior</p>
                                <p className="text-xs text-amber-700 mt-1">
                                  Email changes will be applied directly. Members will be notified of the change.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Summary & Submit Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-transparent rounded-t-xl">
                <h3 className="text-sm font-semibold text-gray-900">Import Summary</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className={`p-3 rounded-lg border ${createInvoices ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                      {createInvoices ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      ) : (
                        <span className="h-5 w-5 rounded-full border-2 border-gray-300"></span>
                      )}
                      <span className={`text-sm font-medium ${createInvoices ? 'text-green-700' : 'text-gray-500'}`}>
                        {createInvoices ? 'Creating invoices' : 'No invoices'}
                      </span>
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg border ${sendEmails ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                      {sendEmails ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      ) : (
                        <span className="h-5 w-5 rounded-full border-2 border-gray-300"></span>
                      )}
                      <span className={`text-sm font-medium ${sendEmails ? 'text-green-700' : 'text-gray-500'}`}>
                        {sendEmails ? 'Sending emails' : 'No emails'}
                      </span>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg border bg-blue-50 border-blue-200">
                    <div className="flex items-center gap-2">
                      <IdentificationIcon className="h-5 w-5 text-blue-500" />
                      <span className="text-sm font-medium text-blue-700">
                        Match by {lookupBy === 'email' ? 'Email' : 'ID'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-200">
                  <a
                    href={membersPath}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Back to Members
                  </a>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-lg transition-colors ${
                      isLoading
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm'
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <CloudArrowUpIcon className="w-5 h-5" />
                        Upload Members
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MemberUploadPage;
